
import { useCallback } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useSession } from "@/hooks/useSession";
import { useToast } from "@/hooks/use-toast";

export function useProgressShares(resourceId?: string) {
  const { session } = useSession();
  const { toast } = useToast();

  const updateResourceShares = useCallback(async (calculatedProgress: number) => {
    if (!resourceId || !session?.user?.id) return;
    
    try {
      const now = new Date().toISOString();
      
      const { data: existingShare, error: shareCheckError } = await supabase
        .from('resource_shares')
        .select('id, status, progress, completed_at')
        .eq('resource_id', resourceId)
        .eq('trainee_id', session.user.id)
        .maybeSingle();
        
      if (shareCheckError) {
        console.error('Error checking for existing resource_shares:', shareCheckError);
        throw shareCheckError;
      }
      
      // If the calculated progress is different from existing, update it
      console.log('Existing share data:', existingShare);
      
      try {
        if (existingShare?.id) {
          // Only update if the progress has changed or is higher
          if (!existingShare.progress || calculatedProgress > existingShare.progress) {
            console.log(`Updating resource_shares progress from ${existingShare.progress || 0}% to ${calculatedProgress}%`);
            
            const updateData: Record<string, any> = { 
              progress: calculatedProgress,
              status: existingShare.status || 'accepted'
            };
            
            // Only set completed_at if we've reached 100% and it's not already set
            if (calculatedProgress === 100 && !existingShare.completed_at) {
              updateData.completed_at = now;
            }
            
            const { error: updateError } = await supabase
              .from('resource_shares')
              .update(updateData)
              .eq('id', existingShare.id);
              
            if (updateError) {
              console.error('Error updating resource_shares:', updateError);
              throw updateError;
            }
          } else {
            console.log(`No need to update progress: current=${existingShare.progress}%, calculated=${calculatedProgress}%`);
          }
        } else {
          console.log(`Creating new resource_shares record with progress: ${calculatedProgress}%`);
          
          const { error: insertError } = await supabase
            .from('resource_shares')
            .insert({ 
              resource_id: resourceId,
              trainee_id: session.user.id,
              status: 'accepted',
              progress: calculatedProgress,
              completed_at: calculatedProgress === 100 ? now : null
            });
            
          if (insertError) {
            console.error('Error inserting resource_shares:', insertError);
            toast({
              title: "Warning",
              description: "Your progress was saved but we couldn't update the overall status.",
            });
          }
        }
      } catch (shareError) {
        console.error('Error updating resource shares:', shareError);
        toast({
          title: "Warning",
          description: "Your progress was saved but we couldn't update the overall status.",
        });
      }
    } catch (error) {
      console.error('Error in updateResourceShares:', error);
      toast({
        title: "Warning",
        description: "We couldn't update your overall progress status.",
      });
    }
  }, [resourceId, session?.user?.id, toast]);

  const calculateProgress = useCallback(async (): Promise<number> => {
    try {
      if (!session?.user?.id || !resourceId) return 0;
      
      const { data: filesData, error: filesError } = await supabase
        .from('resource_files')
        .select('id')
        .eq('resource_id', resourceId);
        
      if (filesError) {
        throw filesError;
      }
      
      if (!filesData) {
        throw new Error('No files found for resource');
      }
      
      const { data: completedData, error: completedError } = await supabase
        .from('training_progress')
        .select('file_id')
        .eq('resource_id', resourceId)
        .eq('user_id', session.user.id)
        .eq('completed', true);
        
      if (completedError) {
        throw completedError;
      }
      
      const totalFiles = filesData.length;
      const completedFiles = completedData?.length || 0;
      const progress = totalFiles > 0 
        ? Math.round((completedFiles / totalFiles) * 100) 
        : 0;
      
      console.log('Progress calculation:', {
        completedFiles,
        totalFiles,
        progress,
      });
      
      return progress;
    } catch (error) {
      console.error('Error calculating progress:', error);
      return 0;
    }
  }, [resourceId, session?.user?.id]);

  return {
    updateResourceShares,
    calculateProgress
  };
}
